import { useState, useEffect, use } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";

// Material Dashboard 2 React themes
import theme from "assets/theme";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav } from "context";

import { useDispatch, useSelector } from "react-redux";
import { Backdrop, CircularProgress } from "@mui/material";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "services/firebase";
// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

import { getLoginedUser, setDeviceIds, firebase, getClientData } from "store/slices/firebaseSlice";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();
  const dispatching = useDispatch();
  const { loading, userLogo, clientId, user } = useSelector(firebase);
  // Cache for the rtl

  useEffect(() => {
    dispatching(getLoginedUser());
  }, []);

  useEffect(() => {
    if (user) {
      dispatching(getClientData(user.uid));
    }
  }, [user]);

  useEffect(() => {
    if (!clientId) return;
    const unsubscribeDevices = onSnapshot(
      collection(db, "Results", clientId, "devices"),
      (devices) => {
        let data = [];
        for (const device of devices.docs) {
          data.push(device.id);
        }
        dispatching(setDeviceIds(data));
      }
    );
    return () => {
      unsubscribeDevices();
    };
  }, [clientId]);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={
              userLogo ||
              ((transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite)
            }
            brandName={userLogo ? "" : "Milk Analyzer"}
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
        </>
      )}
      <Routes>
        {getRoutes(routes)}
        <Route path="*" element={<Navigate to="/tables" />} />
      </Routes>
      <Backdrop open={loading}>
        <CircularProgress color="secondary" />
      </Backdrop>
    </ThemeProvider>
  );
}
